export const DEPARTURE_BUFFER_MINUTES = 15;

export const toDateKey = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return [
      value.getFullYear(),
      String(value.getMonth() + 1).padStart(2, "0"),
      String(value.getDate()).padStart(2, "0"),
    ].join("-");
  }
  const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

export const toLocalDateTime = (
  dateValue,
  timeValue,
  fallbackTime = "00:00",
) => {
  const dateKey = toDateKey(dateValue);
  if (!dateKey) return null;
  const [year, month, day] = dateKey.split("-").map(Number);
  const [hour, minute] = String(timeValue || fallbackTime)
    .slice(0, 5)
    .split(":")
    .map(Number);
  return new Date(year, month - 1, day, hour || 0, minute || 0, 0, 0);
};

export const formatTime = (date) => {
  if (!date) return "--:--";
  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}`;
};

export const formatDateShort = (date) => {
  if (!date) return "";
  const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
  return `${date.getMonth() + 1}/${date.getDate()}(${weekdays[date.getDay()]})`;
};

export const formatDuration = (milliseconds) => {
  const minutes = Math.max(0, Math.ceil(milliseconds / 60000));
  if (minutes < 60) return `${minutes}分`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}時間${rest}分` : `${hours}時間`;
};

export const isTripActiveToday = (shiori, now = new Date()) => {
  const fromKey = toDateKey(shiori?.TRIP_FROM);
  const toKey = toDateKey(shiori?.TRIP_TO);
  const todayKey = toDateKey(now);
  return Boolean(fromKey && toKey && fromKey <= todayKey && todayKey <= toKey);
};

export const buildMapUrl = (item) => {
  const source = item?.source || item;
  const lat = source?.LATITUDE ?? source?.latitude;
  const lng = source?.LONGITUDE ?? source?.longitude;
  if (lat !== null && lat !== undefined && lng !== null && lng !== undefined) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  if (source?.ADDRESS_NMV) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      source.ADDRESS_NMV,
    )}`;
  }
  return null;
};

export const buildTimelineItems = (schedules = []) => {
  const baseItems = schedules
    .filter((schedule) => schedule.NEED_FLG !== "0")
    .map((schedule) => {
      const start = toLocalDateTime(schedule.START_YMD, schedule.START_TIME);
      if (!start) return null;
      const end =
        toLocalDateTime(
          schedule.END_YMD || schedule.START_YMD,
          schedule.END_TIME,
          schedule.START_TIME || "00:00",
        ) || new Date(start.getTime() + 60 * 60000);
      const adjustedEnd =
        end.getTime() <= start.getTime()
          ? new Date(start.getTime() + 60 * 60000)
          : end;

      return {
        source: schedule,
        start,
        end: adjustedEnd,
        title: schedule.SUBJECT_NMV || "予定",
        placeText:
          schedule.ADDRESS_NMV ||
          schedule.TRANSPORTATION_NMV ||
          schedule.REMARKS ||
          "場所情報なし",
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start || a.end - b.end);

  return baseItems.map((item, index, items) => {
    const nextStart = items[index + 1]?.start;
    const effectiveEnd =
      !item.source.END_TIME && nextStart && nextStart > item.start
        ? nextStart
        : item.end;
    return {
      ...item,
      effectiveEnd,
      timeLabel:
        item.source.END_TIME && formatTime(item.start) !== formatTime(item.end)
          ? `${formatTime(item.start)}-${formatTime(item.end)}`
          : formatTime(item.start),
    };
  });
};

export const getTripLiveState = (
  schedules = [],
  shioriData = null,
  now = new Date(),
) => {
  const timelineItems = buildTimelineItems(schedules);
  const currentItem =
    timelineItems.find(
      (item) => now >= item.start && now < item.effectiveEnd,
    ) || null;
  const nextItem = timelineItems.find((item) => item.start > now) || null;
  const previousItem =
    [...timelineItems].reverse().find((item) => item.effectiveEnd <= now) ||
    null;
  const tripStart = toLocalDateTime(shioriData?.TRIP_FROM, "00:00");
  const tripEnd = toLocalDateTime(shioriData?.TRIP_TO, "23:59", "23:59");

  let tripState = "waiting";
  if (tripStart && now < tripStart) tripState = "before-trip";
  else if (tripEnd && now > tripEnd) tripState = "after-trip";
  else if (currentItem) tripState = "current";
  else if (nextItem) tripState = "between";
  else if (previousItem) tripState = "done";

  return {
    timelineItems,
    currentItem,
    nextItem,
    previousItem,
    tripState,
  };
};

export const getProgressPercent = (item, now = new Date()) => {
  if (!item) return 0;
  const total = item.effectiveEnd - item.start;
  if (total <= 0) return 0;
  return Math.min(
    100,
    Math.max(0, Math.round(((now - item.start) / total) * 100)),
  );
};
